/**
 * @copyright Copyright © 2020-2025 Airbus DS SLC. All rights reserved.
 */
class Vertex {
    #name;

    /**
     * Creates a new Vertex.
     * @param {string} name - The name of the vertex.
     */
    constructor(name) {
        this.connections = [];
        this.index = -1;
        this.lowlink = -1;
        this.#name = name;
    }

    /**
     * Gets the name of the vertex.
     * @returns {string} The name of the vertex.
     */
    get name() {
        return this.#name;
    }

    /**
     * Connects this vertex to another vertex.
     * @param {Vertex} vertex - The vertex to connect to.
     */
    connect(vertex) {
        this.connections.push(vertex);
    }

    /**
     * Checks if this vertex is equal to another vertex.
     * @param {Vertex} vertex - The vertex to compare.
     * @returns {boolean} True if the vertices are equal, false otherwise.
     */
    equals(vertex) {
        return this.name === vertex.name;
    }
}

/**
 * Represents a graph.
 */
class Graph {
    /**
     * Creates a new Graph.
     */
    constructor() {
        this.vertices = [];
        this.vertices_by_name = {};
    }

    /**
     * Adds a vertex to the graph.
     * @param {string} name - The name of the vertex to add.
     */
    addVertex(name) {
        if (name in this.vertices_by_name) {
            return;
        }
        const vertex = new Vertex(name);
        this.vertices.push(vertex);
        this.vertices_by_name[name] = vertex;
    }

    /**
     * Connects two vertices in the graph.
     * @param {string} name_v - The name of the first vertex.
     * @param {string} name_w - The name of the second vertex.
     */
    connectVertex(name_v, name_w) {
        if (!(name_v in this.vertices_by_name)) {
            this.addVertex(name_v);
        }
        if (!(name_w in this.vertices_by_name)) {
            this.addVertex(name_w);
        }
        this.vertices_by_name[name_v].connect(this.vertices_by_name[name_w]);
    }
}

/**
 * Represents a stack of vertices.
 */
class VertexStack {
    /**
     * Creates a new VertexStack.
     */
    constructor() {
        this.vertices = [];
        this.vertices_by_name = {};
    }

    /**
     * Checks if the stack contains a vertex.
     * @param {Vertex} vertex - The vertex to check.
     * @returns {boolean} True if the stack contains the vertex, false otherwise.
     */
    contains(vertex) {
        return vertex.name in this.vertices_by_name;
    }

    /**
     * Pushes a vertex onto the stack.
     * @param {Vertex} vertex - The vertex to push.
     */
    push(vertex) {
        if (vertex.name in this.vertices_by_name) {
            return;
        }
        this.vertices.push(vertex);
        this.vertices_by_name[vertex.name] = vertex;
    }

    /**
     * Pops a vertex from the stack.
     * @returns {Vertex|undefined} The popped vertex, or undefined if the stack is empty.
     */
    pop() {
        const vertex = this.vertices.pop();
        if (vertex === undefined) {
            return;
        }
        delete this.vertices_by_name[vertex.name];
        return vertex;
    }

    /**
     * Checks if the stack has vertices.
     * @returns {boolean} True if the stack has vertices, false otherwise.
     */
    has_vertices() {
        return this.vertices.length > 0;
    }
}

/**
 * Implements Tarjan's algorithm for finding strongly connected components in a graph.
 */
export class Tarjan {
    /**
     * Creates a new Tarjan instance.
     */
    constructor() {
        /**
         * @type {Graph}
         */
        this.graph = new Graph();
        /**
         * @type {VertexStack}
         */
        this.stack = new VertexStack();
    }

    /**
     * Runs Tarjan's algorithm to find strongly connected components.
     * @returns {Array<Array<Vertex>>} The strongly connected components.
     */
    run() {
        const state = {scc: [], index: 0}; // state with index and strongly connected component
        for (let vertex of this.graph.vertices) {
            if (vertex.index < 0) {
                this.strongconnect(state, vertex);
            }
        }
        return state.scc;
    }

    /**
     * The strongconnect function for Tarjan's algorithm.
     * @param {Object} state - The state object containing the index and strongly connected components.
     * @param {Vertex} v - The current vertex.
     */
    strongconnect(state, v) {
        v.index = state.index;
        v.lowlink = state.index;
        state.index += 1;
        this.stack.push(v);

        for (let w of v.connections) {
            if (w.index < 0) {
                this.strongconnect(state, w);
                v.lowlink = Math.min(v.lowlink, w.lowlink);
            } else if (this.stack.contains(w)) {
                v.lowlink = Math.min(v.lowlink, w.index);
            }
        }

        if (v.lowlink === v.index) {
            let vertices = [];
            let w;
            if (this.stack.has_vertices()) {
                do {
                    w = this.stack.pop();
                    vertices.push(w);
                } while (!v.equals(w));
            }
            if (vertices.length > 0) {
                state.scc.push(vertices);
            }
        }
    }
}
